import { config } from '@/config'
import i18n from '@/lib/i18n'

export type DocumentAction = 'extract_terms' | 'detect_metadata'

export interface ActionProgress {
  stage: 'document' | 'complete' | 'keepalive'
  percentage: number
  index?: number
  total?: number
  document_id?: number
  title?: string
  status?: 'ok' | 'error'
  detail?: string
  job_id?: number
  done?: number
  failed?: number
  message?: string
}

/**
 * Run an action over documents, streaming per-document progress.
 *
 * Uses fetch rather than axios because the endpoint returns SSE — a batch of
 * twenty documents is about a minute, and a silent wait that long reads as a hang.
 */
export async function runDocumentAction(
  documentIds: number[],
  action: DocumentAction,
  onProgress: (p: ActionProgress) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`${config.BACKEND_URL}/documents/actions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ document_ids: documentIds, action }),
    credentials: 'include',
    signal,
  })
  if (!res.ok || !res.body) {
    const detail = await res.json().catch(() => null)
    throw new Error(detail?.detail ?? i18n.t('errors.actionFailed', { status: res.status }))
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const frames = buffer.split('\n\n')
    buffer = frames.pop() ?? ''
    for (const frame of frames) {
      const line = frame.split('\n').find((l) => l.startsWith('data: '))
      if (!line) continue
      try {
        onProgress(JSON.parse(line.slice(6)))
      } catch {
        /* ignore malformed frame */
      }
    }
  }
}
